import { useState, useRef, useEffect } from "react";
import { rageBaiterImage, pizzazImage } from "@/assets";

/* ─── shared terminal shell ─── */

interface LogLine {
  text: string;
  type: "system" | "input" | "output";
}

interface TerminalProps {
  log: LogLine[];
  done: boolean;
  inputValue: string;
  onChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onRestart: () => void;
  filename: string;
}

function Terminal({ log, done, inputValue, onChange, onSubmit, onRestart, filename }: TerminalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);

  // Scroll the log container itself — never the page
  useEffect(() => {
    const el = logContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [log]);

  // Focus without letting the browser scroll the page
  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, [log, done]);

  return (
    <div className="bg-black rounded-sm border border-border/40 overflow-hidden font-mono text-sm">
      {/* Title bar */}
      <div className="flex items-center gap-2 px-4 py-2 bg-zinc-900 border-b border-zinc-700">
        <span className="w-3 h-3 rounded-full bg-red-500" />
        <span className="w-3 h-3 rounded-full bg-yellow-400" />
        <span className="w-3 h-3 rounded-full bg-green-500" />
        <span className="ml-3 text-xs text-zinc-500 uppercase tracking-widest">{filename}</span>
      </div>

      {/* Log */}
      <div ref={logContainerRef} className="p-5 min-h-56 max-h-96 overflow-y-auto space-y-1">
        {log.map((line, i) =>
          line.text === "" ? (
            <br key={i} />
          ) : (
            <p
              key={i}
              style={{ whiteSpace: "pre-wrap" }}
              className={
                line.type === "system"
                  ? "text-zinc-500"
                  : line.type === "input"
                  ? "text-green-400"
                  : "text-zinc-100"
              }
            >
              {line.text}
            </p>
          )
        )}
      </div>

      {/* Input */}
      <div className="border-t border-zinc-800 px-5 py-3">
        {!done ? (
          <form onSubmit={onSubmit} className="flex items-center gap-2">
            <span className="text-green-400">{">"}</span>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => onChange(e.target.value)}
              className="flex-1 bg-transparent text-zinc-100 outline-none caret-green-400 placeholder:text-zinc-600"
              placeholder="type here..."
              autoComplete="off"
              spellCheck={false}
            />
          </form>
        ) : (
          <button
            onClick={onRestart}
            className="text-xs uppercase tracking-widest text-green-400 hover:text-green-300 transition-colors"
          >
            ↺ Play again
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── RageBaiter 3000 ─── */

type RageStep = "askName" | "askMonica" | "askOffer" | "askCounter" | "askStay" | "askFinal" | "done";

const RAGE_INTRO: LogLine[] = [
  { text: "==================================================", type: "system" },
  { text: "WELCOME TO RAGEBAITER 3000", type: "output" },
  { text: "==================================================", type: "system" },
  { text: "", type: "system" },
  { text: "You are interviewing for a mysterious position at a tiny startup.\nThe CEO has not read your résumé, cannot remember your name, and\nhas already asked someone to make coffee.", type: "output" },
  { text: "", type: "system" },
  { text: "Your goal: leave with your dignity before your rage reaches 10.", type: "output" },
  { text: "", type: "system" },
  { text: "Debra: Welcome! We're thrilled you could make it.", type: "output" },
  { text: "CEO: Debra. Coffee. Oat milk, unless it tastes too much like oats.", type: "output" },
  { text: "Debra: Of course, sir.", type: "output" },
  { text: "CEO: Great. Now... who are you again?", type: "output" },
  { text: "", type: "system" },
  { text: "Enter your name:", type: "output" },
];

function titleCase(s: string): string {
  return s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

function rageMeterLine(rage: number): LogLine {
  const capped = Math.min(rage, 10);
  return {
    text: `[Rage level: ${"!".repeat(capped)}${".".repeat(10 - capped)}] ${capped}/10`,
    type: "system",
  };
}

function RageBaiter3000() {
  const [log, setLog] = useState<LogLine[]>(RAGE_INTRO);
  const [step, setStep] = useState<RageStep>("askName");
  const [inputValue, setInputValue] = useState("");
  const rageRef = useRef(0);
  const nameRef = useRef("Candidate");

  function push(...lines: LogLine[]) {
    setLog((prev) => [...prev, ...lines]);
  }

  function invalid(options: string[]) {
    push({ text: `Please choose one of these options: ${options.join(", ")}.`, type: "output" });
  }

  // Shared "dynamic role" block that runs after the offer branch resolves
  function afterOffer(): void {
    push(
      { text: "", type: "system" },
      rageMeterLine(rageRef.current),
      { text: "", type: "system" },
      { text: "CEO: One last detail: the role is extremely dynamic.", type: "output" },
      { text: "CEO: You will be our marketing coordinator, IT support, office manager,", type: "output" },
      { text: "CEO: social-media strategist, weekend janitor, and occasional emotional support human.", type: "output" },
      { text: "CEO: The position is fully on-site, except when we need you to work from home.", type: "output" },
      { text: "CEO: Also, you'll share a desk with Darin. He takes calls on speakerphone.", type: "output" },
      { text: "'Darin' yells from the darkest corner of the office: MY NAME. IS. SETH!!!!\nThe CEO ignores him.", type: "output" }
    );
    rageRef.current += 2;
    push({ text: "", type: "system" }, rageMeterLine(rageRef.current), { text: "", type: "system" });

    if (rageRef.current >= 10) {
      push(
        { text: "You stare silently at the CEO for several seconds.", type: "output" },
        { text: "Then you stand up, walk out, and buy yourself an excellent coffee.", type: "output" },
        { text: "", type: "system" },
        { text: "GAME OVER: You quit before becoming a workplace legend.", type: "output" },
        { text: "", type: "system" },
        { text: "Thanks for playing RageBaiter 3000!", type: "output" }
      );
      setStep("done");
    } else {
      push({ text: "Do you stay for the final question? Y or N:", type: "output" });
      setStep("askStay");
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const raw = inputValue;
    const val = raw.trim().toLowerCase();
    if (!raw.trim() && step !== "askName") return;
    setInputValue("");

    switch (step) {
      case "askName": {
        const name = raw.trim() ? titleCase(raw.trim()) : "Candidate";
        nameRef.current = name;
        rageRef.current = 1;
        push(
          { text: `> ${raw.trim() || ""}`, type: "input" },
          { text: `CEO: Right. ${name}. Interesting choice.`, type: "output" },
          { text: "CEO: I usually prefer shorter names. Easier to forget.", type: "output" },
          { text: "", type: "system" },
          rageMeterLine(rageRef.current),
          { text: "", type: "system" },
          { text: "CEO: Before we begin, can I call you Monica instead?", type: "output" },
          { text: "Choose Y or N:", type: "output" }
        );
        setStep("askMonica");
        break;
      }

      case "askMonica": {
        push({ text: `> ${raw.trim()}`, type: "input" });
        if (val !== "y" && val !== "n") {
          invalid(["y", "n"]);
          return;
        }
        if (val === "y") {
          push(
            { text: "CEO: Wonderful. Monica is such a collaborative name.", type: "output" },
            { text: "CEO: We value flexibility more than credentials here.", type: "output" }
          );
        } else {
          push(
            { text: `CEO: Hmm. Very firm boundaries for a ${nameRef.current}.`, type: "output" },
            { text: "CEO: That may be difficult for our culture.", type: "output" }
          );
          rageRef.current += 1;
        }
        push(
          { text: "", type: "system" },
          rageMeterLine(rageRef.current),
          { text: "", type: "system" },
          { text: "CEO: Let's discuss compensation.", type: "output" },
          { text: "CEO: We were considering $90,000, assuming you own a car,", type: "output" },
          { text: "CEO: a laptop, and possibly a small apartment we can use as office space.", type: "output" },
          { text: "Accept, negotiate, or decline?", type: "output" }
        );
        setStep("askOffer");
        break;
      }

      case "askOffer": {
        push({ text: `> ${raw.trim()}`, type: "input" });
        if (val !== "accept" && val !== "negotiate" && val !== "decline") {
          invalid(["accept", "negotiate", "decline"]);
          return;
        }
        if (val === "accept") {
          push(
            { text: "CEO: Excellent. I knew you had a realistic understanding of your worth.", type: "output" },
            { text: "CEO: Unfortunately, the budget has changed while you were agreeing.", type: "output" },
            { text: "CEO: The offer is now $45,000.", type: "output" }
          );
          rageRef.current += 2;
          afterOffer();
        } else if (val === "negotiate") {
          push({ text: "What is your counteroffer?", type: "output" });
          setStep("askCounter");
        } else {
          push(
            { text: "CEO: Decline? That's okay.", type: "output" },
            { text: "CEO: We prefer candidates who are passionate enough to ignore numbers.", type: "output" },
            { text: "CEO: The offer is now $45,000, should you become passionate later.", type: "output" }
          );
          rageRef.current += 1;
          afterOffer();
        }
        break;
      }

      case "askCounter": {
        push(
          { text: `> ${raw.trim()}`, type: "input" },
          { text: `CEO: ${raw.trim()}? Fascinating.`, type: "output" },
          { text: "CEO: I admire your confidence. We will not be doing that.", type: "output" },
          { text: "CEO: Our revised offer is $45,000 and a branded water bottle.", type: "output" }
        );
        rageRef.current += 2;
        afterOffer();
        break;
      }

      case "askStay": {
        push({ text: `> ${raw.trim()}`, type: "input" });
        if (val !== "y" && val !== "n") {
          invalid(["y", "n"]);
          return;
        }
        if (val === "n") {
          push(
            { text: `CEO: Leaving already, ${nameRef.current}?`, type: "output" },
            { text: "Seth: Take me with you...please.", type: "output" },
            { text: "CEO: That's disappointing. You had such strong 'unpaid overtime' energy.", type: "output" },
            { text: "", type: "system" },
            { text: "YOU WIN: You leave with your dignity, your résumé, and your sanity.", type: "output" },
            { text: "", type: "system" },
            { text: "Thanks for playing RageBaiter 3000!", type: "output" }
          );
          setStep("done");
        } else {
          push(
            { text: `CEO: Excellent, Deshawn—sorry, ${nameRef.current}.`, type: "output" },
            { text: "CEO: Can you start tomorrow at 6:00 a.m.?", type: "output" },
            { text: "CEO: Actually, tonight would show initiative.", type: "output" },
            { text: "", type: "system" },
            { text: "Accept the job or walk away?", type: "output" }
          );
          setStep("askFinal");
        }
        break;
      }

      case "askFinal": {
        push({ text: `> ${raw.trim()}`, type: "input" });
        if (val !== "accept" && val !== "walk") {
          invalid(["accept", "walk"]);
          return;
        }
        if (val === "accept") {
          push(
            { text: "CEO: Congratulations. You're exactly the kind of person we're looking for.", type: "output" },
            { text: "CEO: Debra will email your onboarding packet.", type: "output" },
            { text: "CEO: It is 84 pages long and begins with a nondisclosure agreement.", type: "output" },
            { text: "", type: "system" },
            { text: "GAME OVER: The CEO is pleased. This is somehow worse than losing.", type: "output" }
          );
        } else {
          push(
            { text: "CEO: Wow. Setting boundaries in this economy?", type: "output" },
            { text: "CEO: Bold.", type: "output" },
            { text: "", type: "system" },
            { text: "YOU WIN: You walk away before the onboarding packet finds you.", type: "output" }
          );
        }
        push(
          { text: "", type: "system" },
          { text: "Thanks for playing RageBaiter 3000!", type: "output" }
        );
        setStep("done");
        break;
      }
    }
  }

  function handleRestart() {
    setLog(RAGE_INTRO);
    setStep("askName");
    setInputValue("");
    rageRef.current = 0;
    nameRef.current = "Candidate";
  }

  return (
    <Terminal
      log={log}
      done={step === "done"}
      inputValue={inputValue}
      onChange={setInputValue}
      onSubmit={handleSubmit}
      onRestart={handleRestart}
      filename="ragebaiter3000.py"
    />
  );
}

/* ─── Pizza-z's Pizza ─── */

type PizzaStep = "askSize" | "askPepperoni" | "askCheese" | "askLacto" | "done";

const PIZZA_INTRO: LogLine[] = [
  { text: "Welcome to Pizza-z's Pizza!", type: "output" },
  { text: "We have a variety of sizes and toppings to choose from. Please feel free to customize your pizza order to your liking. Let's get started!", type: "output" },
  { text: "", type: "system" },
  { text: "What size pizza do you want? S, M, L, XL or LAWD!:", type: "output" },
];

function PizzazPizza() {
  const [log, setLog] = useState<LogLine[]>(PIZZA_INTRO);
  const [step, setStep] = useState<PizzaStep>("askSize");
  const [inputValue, setInputValue] = useState("");
  const [bill, setBill] = useState(0);
  const [pepperoni, setPepperoni] = useState(false);
  const [cheese, setCheese] = useState(false);

  function push(...lines: LogLine[]) {
    setLog((prev) => [...prev, ...lines]);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const val = inputValue.trim();
    if (!val) return;
    setInputValue("");

    switch (step) {
      case "askSize": {
        const size = val.toUpperCase();
        const prices: Record<string, number> = { S: 6, M: 9, L: 12, XL: 15, "LAWD!": 20 };
        const base = prices[size] ?? 0;
        setBill(base);
        const lines: LogLine[] = [{ text: `> ${val}`, type: "input" }];
        if (size === "LAWD!") lines.push({ text: "Must sign disclosure!", type: "output" });
        if (!prices[size]) lines.push({ text: "Invalid size selected. Starting with a default base of $0.", type: "output" });
        lines.push({ text: "Do you want pepperoni on your pizza? Y or N:", type: "output" });
        push(...lines);
        setStep("askPepperoni");
        break;
      }

      case "askPepperoni": {
        const y = val.toUpperCase() === "Y";
        setPepperoni(y);
        setBill((b) => b + (y ? 3 : 0));
        push(
          { text: `> ${val}`, type: "input" },
          { text: "Do you want extra cheese? Y or N:", type: "output" }
        );
        setStep("askCheese");
        break;
      }

      case "askCheese": {
        const y = val.toUpperCase() === "Y";
        setCheese(y);
        setBill((b) => b + (y ? 2.5 : 0));
        push(
          { text: `> ${val}`, type: "input" },
          { text: "Would you like to try the lacto-challenger? Y or N:", type: "output" }
        );
        setStep("askLacto");
        break;
      }

      case "askLacto": {
        const y = val.toUpperCase() === "Y";
        // Compute final bill from current state + lacto
        setBill((prev) => {
          const subtotal = prev + (y ? 5.75 : 0);
          const tax = subtotal * 0.06;
          const total = subtotal + tax;
          const lines: LogLine[] = [{ text: `> ${val}`, type: "input" }];
          if (y) lines.push({ text: "Get your Lactaid!", type: "output" });
          lines.push(
            { text: `Your total will be: $${total.toFixed(2)}`, type: "output" },
            { text: "See you in 20 minutes!", type: "output" },
            { text: "", type: "system" },
            { text: "Thank you for ordering!", type: "output" }
          );
          setLog((l) => [...l, ...lines]);
          return subtotal;
        });
        setStep("done");
        break;
      }
    }
  }

  function handleRestart() {
    setLog(PIZZA_INTRO);
    setStep("askSize");
    setInputValue("");
    setBill(0);
    setPepperoni(false);
    setCheese(false);
  }

  return (
    <Terminal
      log={log}
      done={step === "done"}
      inputValue={inputValue}
      onChange={setInputValue}
      onSubmit={handleSubmit}
      onRestart={handleRestart}
      filename="pizzaz_pizza.py"
    />
  );
}

/* ─── Games page ─── */

interface GameSectionProps {
  index: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  tip: React.ReactNode;
  children: React.ReactNode;
  bordered?: boolean;
}

function GameSection({ index, title, description, image, imageAlt, tip, children, bordered }: GameSectionProps) {
  return (
    <section className={`py-24 bg-background ${bordered ? "border-b border-border/40" : ""}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Header row — identical for every game */}
        <div className="flex items-baseline gap-6 mb-4">
          <span className="text-xl md:text-2xl font-serif text-muted-foreground">{index}</span>
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground border border-border/60 px-2 py-1 rounded-sm">
              Text-style prompt
            </span>
            <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground mt-4">
              {title}
            </h2>
          </div>
        </div>
        <p className="text-muted-foreground leading-relaxed max-w-3xl mb-12 md:ml-[calc(theme(spacing.6)+2ch)]">
          {description}
        </p>

        {/* Image + terminal — aligned tops, equal visual weight */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
          <div className="rounded-sm border border-border/40 overflow-hidden bg-black">
            <img src={image} alt={imageAlt} className="w-full h-full object-cover" />
          </div>
          <div className="flex flex-col">
            <div className="flex-1 flex flex-col [&>div]:flex-1 [&>div]:flex [&>div]:flex-col [&>div>div:nth-child(2)]:flex-1">
              {children}
            </div>
            <p className="text-xs text-muted-foreground mt-4">{tip}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Games() {
  return (
    <div className="flex flex-col pt-10">
      {/* Page Header */}
      <section className="pt-20 pb-16 bg-background border-b border-border/40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">Interactive</p>
          <h1 className="text-5xl md:text-7xl font-serif font-light text-foreground leading-[0.9] tracking-tight mb-8">
            Games.
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
            A growing collection of text-based and interactive experiences built for curiosity, chaos, and fun.
          </p>
        </div>
      </section>

      <GameSection
        index="01"
        title="RageBaiter 3000"
        description="A short, absurdist office encounter. Enter the building, interview with a chaotic CEO, and decide how much you can take before walking out. Built in Python — now playable right in the browser."
        image={rageBaiterImage}
        imageAlt="RageBaiter 3000 — pixel art office interview scene"
        tip={
          <>
            Tip: when asked about salary, try typing{" "}
            <span className="font-mono text-foreground">negotiate</span>.
          </>
        }
        bordered
      >
        <RageBaiter3000 />
      </GameSection>

      <GameSection
        index="02"
        title="Pizza-z's Pizza"
        description="Build your perfect pizza — choose your size, pick your toppings, and dare to try the Lacto-Challenger. Three pounds of cheese. You've been warned. Built in Python — playable right here."
        image={pizzazImage}
        imageAlt="Pizza-z's Pizza — pixel art pizza arcade screen"
        tip={
          <>
            Tip: sizes are <span className="font-mono text-foreground">S</span>,{" "}
            <span className="font-mono text-foreground">M</span>,{" "}
            <span className="font-mono text-foreground">L</span>,{" "}
            <span className="font-mono text-foreground">XL</span>, or{" "}
            <span className="font-mono text-foreground">LAWD!</span> if you're feeling brave.
          </>
        }
      >
        <PizzazPizza />
      </GameSection>
    </div>
  );
}
